import { useOrganizationList } from "@clerk/clerk-react";
import { useEffect, useMemo, useState } from "react";
import {
	fetchAdminAudit,
	fetchAdminOverview,
	fetchAdminStaffing,
	fetchAdminTenant,
	fetchAdminTenants,
	type AdminAuditEntry,
	type AdminOverview,
	type AdminStaffing,
	type AdminTenant,
	type AdminTenantDetail,
} from "./admin-api.js";
import {
	firmKitStatusLabel,
	firmKitSummary,
	loadFirmKit,
	updateFirmKitChannel,
	type FirmKitChannel,
	type FirmKitChannelStatus,
	type FirmKitRow,
} from "./firm-kit.js";
import { AgentAvatar } from "./AgentAvatar.js";
import { useAppMode } from "./app-mode.js";
import { cn } from "./cn.js";
import { AGENTS, agentById, type AgentId } from "./nav.js";

function formatWhen(iso: string): string {
	try {
		return new Intl.DateTimeFormat(undefined, {
			month: "short",
			day: "numeric",
			hour: "numeric",
			minute: "2-digit",
		}).format(new Date(iso));
	} catch {
		return iso;
	}
}

function ErrorBanner({ error }: { error: string }) {
	return (
		<p className="admin-error" role="alert">
			{error === "forbidden"
				? "Admin API denied — confirm this email is allowlisted on the platform too."
				: error === "network_error"
					? "Couldn’t reach the backend. Is npm run dev:backend running?"
					: `Something went wrong (${error}).`}
		</p>
	);
}

export function AdminHomePanel() {
	const [overview, setOverview] = useState<AdminOverview | null>(null);
	const [kit, setKit] = useState<FirmKitRow[]>(() => loadFirmKit());
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		void (async () => {
			const result = await fetchAdminOverview();
			if (cancelled) return;
			if (!result.ok) {
				setError(result.error);
				return;
			}
			setOverview(result.data);
			setKit(loadFirmKit());
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	const summary = firmKitSummary(kit);

	if (error) return <ErrorBanner error={error} />;
	if (!overview) {
		return <p className="admin-loading">Loading firm pulse…</p>;
	}

	return (
		<div className="admin-panel">
			<div className="admin-stat-row">
				<div className="admin-stat">
					<p className="admin-stat-label">Tenants</p>
					<p className="admin-stat-value">{overview.counts.tenants}</p>
					<p className="admin-stat-hint">Client workspaces</p>
				</div>
				<div className="admin-stat">
					<p className="admin-stat-label">Live connections</p>
					<p className="admin-stat-value">
						{overview.counts.liveConnections}
					</p>
					<p className="admin-stat-hint">Healthy / degraded with tokens</p>
				</div>
				<div className="admin-stat">
					<p className="admin-stat-label">Pending approvals</p>
					<p className="admin-stat-value">
						{overview.counts.pendingApprovals}
					</p>
					<p className="admin-stat-hint">Across every tenant</p>
				</div>
			</div>

			<div className="admin-stat-row">
				<div className="admin-stat">
					<p className="admin-stat-label">Agents enabled</p>
					<p className="admin-stat-value">
						{overview.counts.enabledAgents}
					</p>
					<p className="admin-stat-hint">Staffed DO instances</p>
				</div>
				<div className="admin-stat">
					<p className="admin-stat-label">Kit · email</p>
					<p className="admin-stat-value">
						{summary.emailReady}/{summary.totalAgents}
					</p>
					<p className="admin-stat-hint">Workspace-ready or connected</p>
				</div>
				<div className="admin-stat">
					<p className="admin-stat-label">Kit · LinkedIn</p>
					<p className="admin-stat-value">
						{summary.linkedinReady}/{summary.totalAgents}
					</p>
					<p className="admin-stat-hint">Employee profiles</p>
				</div>
			</div>

			<section className="admin-section">
				<h2 className="admin-section-title">Recent audit</h2>
				{overview.recentAudit.length === 0 ? (
					<p className="admin-empty">No audit events yet.</p>
				) : (
					<ul className="admin-feed">
						{overview.recentAudit.map((entry) => (
							<li key={entry.id} className="admin-feed-row">
								<div className="admin-feed-main">
									<p className="admin-feed-title">
										{entry.event}
										<span className="admin-feed-outcome">
											{entry.outcome}
										</span>
									</p>
									<p className="admin-feed-meta">
										{entry.tenantName ?? "Unknown tenant"}
										{" · "}
										{entry.actor}
										{entry.tool ? ` · ${entry.tool}` : ""}
									</p>
								</div>
								<time className="admin-feed-when" dateTime={entry.at}>
									{formatWhen(entry.at)}
								</time>
							</li>
						))}
					</ul>
				)}
			</section>
		</div>
	);
}

export function AdminRosterPanel() {
	const [staffing, setStaffing] = useState<AdminStaffing | null>(null);
	const [kit, setKit] = useState<FirmKitRow[]>(() => loadFirmKit());
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		void (async () => {
			const result = await fetchAdminStaffing();
			if (cancelled) return;
			if (!result.ok) {
				setError(result.error);
				return;
			}
			setStaffing(result.data);
			setKit(loadFirmKit());
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	const byAgent = useMemo(() => {
		const map = new Map<string, string[]>();
		for (const row of staffing?.assignments ?? []) {
			const list = map.get(row.agentType) ?? [];
			list.push(row.tenantName);
			map.set(row.agentType, list);
		}
		return map;
	}, [staffing]);

	if (error) return <ErrorBanner error={error} />;

	return (
		<div className="admin-panel">
			<p className="admin-intro">
				Employees of Mairko — public persona stays at the firm; staffing and
				memory stay per tenant.
			</p>
			<ul className="admin-roster">
				{AGENTS.map((agent) => {
					const email =
						kit
							.find((row) => row.agentId === agent.id)
							?.channels.find((channel) => channel.channel === "email")
							?.identity ?? "—";
					const desks = byAgent.get(agent.id) ?? [];
					return (
						<li key={agent.id} className="admin-roster-card">
							<AgentAvatar agent={agent} size="md" />
							<div className="admin-roster-copy">
								<div className="admin-roster-title-row">
									<p className="admin-roster-name">{agent.name}</p>
									<span
										className={cn(
											"integration-badge",
											agent.availability === "live" &&
												"integration-badge--ok",
										)}
									>
										{agent.availability === "live" ? "Live" : "Soon"}
									</span>
								</div>
								<p className="admin-roster-role">
									{agent.department} · {agent.title}
								</p>
								<p className="admin-roster-email">{email}</p>
								<p className="admin-roster-desks">
									{staffing === null
										? "Loading desks…"
										: desks.length === 0
											? "Not staffed on any desk yet"
											: `On ${desks.length} desk${desks.length === 1 ? "" : "s"}: ${desks.join(", ")}`}
								</p>
							</div>
						</li>
					);
				})}
			</ul>
		</div>
	);
}

const KIT_STATUSES: FirmKitChannelStatus[] = [
	"planned",
	"workspace_ready",
	"oauth_pending",
	"connected",
	"error",
];

export function AdminKitPanel() {
	const [kit, setKit] = useState<FirmKitRow[]>(() => loadFirmKit());
	const [filter, setFilter] = useState<FirmKitChannel | "all">("all");
	const summary = firmKitSummary(kit);

	const setStatus = (
		agentId: AgentId,
		channel: FirmKitChannel,
		status: FirmKitChannelStatus,
	) => {
		setKit(updateFirmKitChannel(agentId, channel, { status }));
	};

	return (
		<div className="admin-panel">
			<p className="admin-intro">
				Outside tools for each employee. Connection status is tracked here
				until firm sign-in (Workspace / LinkedIn) is live.
			</p>

			<div className="admin-stat-row">
				<div className="admin-stat">
					<p className="admin-stat-label">Email ready</p>
					<p className="admin-stat-value">
						{summary.emailReady}/{summary.totalAgents}
					</p>
				</div>
				<div className="admin-stat">
					<p className="admin-stat-label">LinkedIn ready</p>
					<p className="admin-stat-value">
						{summary.linkedinReady}/{summary.totalAgents}
					</p>
				</div>
				<div className="admin-stat">
					<p className="admin-stat-label">Phone ready</p>
					<p className="admin-stat-value">
						{summary.phoneReady}/{summary.totalAgents}
					</p>
				</div>
			</div>

			<div className="agents-filter admin-kit-filter" role="group">
				{(
					[
						{ id: "all" as const, label: "All" },
						{ id: "email" as const, label: "Email" },
						{ id: "linkedin" as const, label: "LinkedIn" },
						{ id: "phone" as const, label: "Phone" },
					] as const
				).map((entry) => (
					<button
						key={entry.id}
						type="button"
						className="agents-filter-btn"
						aria-pressed={filter === entry.id}
						onClick={() => setFilter(entry.id)}
					>
						{entry.label}
					</button>
				))}
			</div>

			<ul className="admin-kit-list">
				{kit.map((row) => {
					const agent = agentById(row.agentId);
					const channels =
						filter === "all"
							? row.channels
							: row.channels.filter((channel) => channel.channel === filter);
					if (channels.length === 0) return null;
					return (
						<li key={row.agentId} className="admin-kit-card">
							<div className="admin-kit-head">
								<AgentAvatar agent={agent} size="sm" />
								<div>
									<p className="admin-kit-name">{agent.name}</p>
									<p className="admin-kit-dept">{agent.department}</p>
								</div>
							</div>
							<ul className="admin-kit-channels">
								{channels.map((channel) => (
									<li
										key={channel.channel}
										className="admin-kit-channel"
									>
										<div className="admin-kit-channel-copy">
											<p className="admin-kit-channel-label">
												{channel.channel}
											</p>
											<p className="admin-kit-channel-id">
												{channel.identity || "— not assigned —"}
											</p>
										</div>
										<label className="admin-kit-status">
											<span className="sr-only">Status</span>
											<select
												value={channel.status}
												onChange={(event) =>
													setStatus(
														row.agentId,
														channel.channel,
														event.target.value as FirmKitChannelStatus,
													)
												}
											>
												{KIT_STATUSES.map((status) => (
													<option key={status} value={status}>
														{firmKitStatusLabel(status)}
													</option>
												))}
											</select>
										</label>
									</li>
								))}
							</ul>
						</li>
					);
				})}
			</ul>
		</div>
	);
}

export function AdminTenantsPanel() {
	const { enterMatrix } = useAppMode();
	const { setActive } = useOrganizationList({
		userMemberships: { infinite: true },
	});
	const [tenants, setTenants] = useState<AdminTenant[] | null>(null);
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [detail, setDetail] = useState<AdminTenantDetail | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [detailError, setDetailError] = useState<string | null>(null);
	const [switching, setSwitching] = useState(false);

	useEffect(() => {
		let cancelled = false;
		void (async () => {
			const result = await fetchAdminTenants();
			if (cancelled) return;
			if (!result.ok) {
				setError(result.error);
				return;
			}
			setTenants(result.data.tenants);
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	useEffect(() => {
		if (!selectedId) {
			setDetail(null);
			return;
		}
		let cancelled = false;
		setDetailError(null);
		void (async () => {
			const result = await fetchAdminTenant(selectedId);
			if (cancelled) return;
			if (!result.ok) {
				setDetailError(result.error);
				setDetail(null);
				return;
			}
			setDetail(result.data);
		})();
		return () => {
			cancelled = true;
		};
	}, [selectedId]);

	const openDesk = async (tenant: AdminTenant) => {
		if (!tenant.clerkOrgId || !setActive) return;
		setSwitching(true);
		try {
			await setActive({ organization: tenant.clerkOrgId });
			enterMatrix();
		} finally {
			setSwitching(false);
		}
	};

	if (error) return <ErrorBanner error={error} />;
	if (!tenants) {
		return <p className="admin-loading">Loading tenants…</p>;
	}

	return (
		<div className="admin-panel admin-panel--split">
			<div className="admin-split-list">
				<p className="admin-intro">
					Every client desk. Opening one switches your Clerk org and returns
					you to Matrix — treat it as break-glass.
				</p>
				{tenants.length === 0 ? (
					<p className="admin-empty">No tenants yet.</p>
				) : (
					<ul className="admin-tenant-list">
						{tenants.map((tenant) => (
							<li key={tenant.id}>
								<button
									type="button"
									className={cn(
										"admin-tenant-row",
										selectedId === tenant.id && "admin-tenant-row--active",
									)}
									onClick={() => setSelectedId(tenant.id)}
								>
									<span className="admin-tenant-name">{tenant.name}</span>
									<span className="admin-tenant-meta">
										{tenant.connectionCount} conn · {tenant.enabledAgentCount}{" "}
										agents · {tenant.memberCount} members
									</span>
								</button>
							</li>
						))}
					</ul>
				)}
			</div>

			<div className="admin-split-detail">
				{!selectedId ? (
					<p className="admin-empty">Select a tenant to inspect.</p>
				) : detailError ? (
					<ErrorBanner error={detailError} />
				) : !detail ? (
					<p className="admin-loading">Loading desk…</p>
				) : (
					<div className="admin-tenant-detail">
						<div className="admin-tenant-detail-head">
							<div>
								<p className="admin-eyebrow">Tenant</p>
								<h2 className="admin-tenant-detail-name">
									{detail.tenant.name}
								</h2>
								<p className="admin-feed-meta">
									{detail.tenant.domain ?? "No domain"}
									{" · "}
									{detail.tenant.plan}
									{" · "}
									{detail.tenant.status}
									{" · "}
									{detail.pendingApprovals} pending approvals
								</p>
							</div>
							{detail.tenant.clerkOrgId ? (
								<button
									type="button"
									className="btn btn-accent"
									disabled={switching}
									onClick={() => {
										const row = tenants.find(
											(tenant) => tenant.id === detail.tenant.id,
										);
										if (row) void openDesk(row);
									}}
								>
									{switching ? "Switching…" : "Open in Matrix"}
								</button>
							) : (
								<p className="admin-feed-meta">No Clerk org linked</p>
							)}
						</div>

						<section className="admin-section">
							<h3 className="admin-section-title">Members</h3>
							<ul className="admin-plain-list">
								{detail.members.map((member) => (
									<li key={member.email}>
										{member.email}
										<span className="admin-muted">
											{" "}
											· {member.roles.join(", ") || "member"}
										</span>
									</li>
								))}
							</ul>
						</section>

						<section className="admin-section">
							<h3 className="admin-section-title">Connections</h3>
							{detail.connections.length === 0 ? (
								<p className="admin-muted">None</p>
							) : (
								<ul className="admin-plain-list">
									{detail.connections.map((connection) => (
										<li key={connection.provider}>
											{connection.provider}
											<span className="admin-muted">
												{" "}
												· {connection.health}
												{connection.live ? " · live" : ""}
												{connection.externalAccountId
													? ` · ${connection.externalAccountId}`
													: ""}
											</span>
										</li>
									))}
								</ul>
							)}
						</section>

						<section className="admin-section">
							<h3 className="admin-section-title">Agents</h3>
							{detail.agents.length === 0 ? (
								<p className="admin-muted">None provisioned</p>
							) : (
								<ul className="admin-plain-list">
									{detail.agents.map((agent) => (
										<li key={agent.agentType}>
											{agent.agentType}
											<span className="admin-muted">
												{" "}
												· {agent.enabled ? "enabled" : "disabled"}
											</span>
										</li>
									))}
								</ul>
							)}
						</section>

						<p className="admin-viewed-by">
							Viewed by {detail.viewedBy} — stay in audit mindset.
						</p>
					</div>
				)}
			</div>
		</div>
	);
}

export function AdminAuditPanel() {
	const [entries, setEntries] = useState<AdminAuditEntry[] | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		void (async () => {
			const result = await fetchAdminAudit(80);
			if (cancelled) return;
			if (!result.ok) {
				setError(result.error);
				return;
			}
			setEntries(result.data.entries);
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	if (error) return <ErrorBanner error={error} />;
	if (!entries) {
		return <p className="admin-loading">Loading audit log…</p>;
	}

	return (
		<div className="admin-panel">
			<p className="admin-intro">
				Workspace and system events across clients. Read-only.
			</p>
			{entries.length === 0 ? (
				<p className="admin-empty">Nothing recorded yet.</p>
			) : (
				<ul className="admin-feed">
					{entries.map((entry) => (
						<li key={entry.id} className="admin-feed-row">
							<div className="admin-feed-main">
								<p className="admin-feed-title">
									{entry.event}
									<span className="admin-feed-outcome">{entry.outcome}</span>
								</p>
								<p className="admin-feed-meta">
									{entry.tenantName ?? entry.tenantId}
									{" · "}
									{entry.actor}
									{entry.tool ? ` · ${entry.tool}` : ""}
									{entry.tier ? ` · ${entry.tier}` : ""}
								</p>
							</div>
							<time className="admin-feed-when" dateTime={entry.at}>
								{formatWhen(entry.at)}
							</time>
						</li>
					))}
				</ul>
			)}
		</div>
	);
}
